// The person's profile on the screen: name, picture, language, time zone.
//
// Promise: `profileQuery` is the one place the profile is read; a change of the
// language reloads the page in that language (see i18n/locale.ts), a change of
// the time zone re-renders every date.
import {
  hashKey,
  type QueryClient,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type { Locale } from "@trameria/i18n";
import { useApi, useMeApp } from "../app/context";
import type { Api } from "../data/api";
import { optimisticMutation } from "../data/optimistic";
import { fileUrl } from "../documents/blocks/upload";
import { syncAccountLocale } from "../i18n/locale";
import {
  formatDateTime,
  formatDay,
  formatDayLong,
  formatRelativeDay,
  formatTime,
} from "../time/format";

export interface Profile {
  id: string;
  name: string;
  /** The picture's file, or null for initials. */
  imageFileId: string | null;
  locale: Locale | null;
  timeZone: string | null;
}

export type ProfilePatch = Partial<
  Pick<Profile, "name" | "imageFileId" | "locale" | "timeZone">
>;

export const profileKey = ["me", "profile"] as const;

export const profileQuery = (api: Api) =>
  queryOptions({
    queryKey: profileKey,
    queryFn: () => api<Profile>("/api/me/profile"),
  });

export function useProfile() {
  return useQuery(profileQuery(useApi()));
}

export function useUpdateProfile() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: ProfilePatch) =>
      api<Profile>("/api/me/profile", { method: "PATCH", body: patch }),
    ...optimisticMutation<Profile, ProfilePatch>(
      qc,
      profileKey,
      (old, patch) => (old ? { ...old, ...patch } : old),
    ),
  });
}

/** The account's picture as an image URL, or null for initials. */
export function useProfilePicture(): string | null {
  const id = useProfile().data?.imageFileId;
  const { apiBase } = useMeApp();
  return id ? fileUrl(apiBase, id) : null;
}

/**
 * Follows the account's language from the profiles the server answers, not
 * from the copy restored after a reload (it may still hold the language
 * before a change) nor from an optimistic write. Returns the function that stops.
 */
export function followAccountLocale(
  qc: QueryClient,
  sync: (locale: Locale | null) => void = syncAccountLocale,
): () => void {
  return qc.getQueryCache().subscribe((event) => {
    if (
      event.type === "updated" &&
      event.action.type === "success" &&
      !event.action.manual &&
      event.query.queryHash === profileHash
    )
      sync((event.action.data as Profile).locale);
  });
}

const profileHash = hashKey(profileKey);

/** The account's time zone, or undefined for the browser's. */
export function useTimeZone(): string | undefined {
  return useProfile().data?.timeZone ?? undefined;
}

/** Date formatters bound to the account's time zone. */
export function useFormat() {
  const timeZone = useTimeZone();
  return {
    timeZone,
    dateTime: (iso: string) => formatDateTime(iso, { timeZone }),
    time: (iso: string) => formatTime(iso, { timeZone }),
    day: (iso: string) => formatDay(iso, { timeZone }),
    longDay: (iso: string) => formatDayLong(iso, { timeZone }),
    relativeDay: (iso: string) => formatRelativeDay(iso, { timeZone }),
  };
}
